import { useState, useMemo, useEffect } from "react";
import { useGetDataQuery } from "../../../utils/react-query-hooks/getData";
import { useGetQueryData } from "../../../utils/react-query-hooks/general";
import { useElemInView } from "../../../utils/customHooks/useElemInView";
import { applyFilterConfigsAsPerTenant } from "./utils/applyFilterConfigsAsPerTenant";
import { applyKeywordSearch } from "./utils/applyKeywordSearch";
import { applyActiveSort } from "./utils/applyActiveSort";
import { extractAllBlocksFromTpl, blockRegistry, isTplAnnotationEnabled } from "@okf/ootils";
import { getAnnoExplorerConfigsForThisTenant } from "./utils/getAnnoExplorerConfigsForThisTenant";
import { getTenant } from "@/TENANT_CONFIGS";
import { useAutoGenFilterConfigsFromTpl } from "../utils/useAutoGenFilterConfigsFromTpl";
import { cleanStaleActiveFilters } from "@/components/discovery/FilterModule/utils/cleanStaleActiveFilters";
import { useSavedTplSelection } from "@/utils/customHooks/useSavedTplSelection";

export const getTplsWithAnnosEnabled = ({ allTpls }) => {
  return allTpls.filter(isTplAnnotationEnabled);
};


const DEFAULT_SORT_OPTIONS = [
  {display: 'Latest', value: { updatedAt: -1 } },
  {display: "Oldest", value: { updatedAt: 1 } },
  {display: 'Alphabetical - A to Z', value: { "annotations.fragment.allText" : 1 }},
  // {display: 'Anno Tags - Highest to Lowest', value: 'annoTagsCountDesc'},
];

export const useGetAnnotationExplorerData = (config = {}) => {
  const {
    presetActiveFilters = [],
    contentTypesWhitelist,
    initialCardsLimit = 20,
    filterDefs : _filterDefs, //remember this is v2 configs, ie the whitelist pattern
    dataSource="annotations"
  } = config;

  // Get all templates and profile types
  const allTpls = useGetQueryData("allTpls");

  // Determine template options with whitelist and annotations enabled
  const tplOptions = useMemo(() => {
    // First, filter by whitelist if exists
    const filteredTpls =
      contentTypesWhitelist?.length > 0
        ? allTpls.filter((d) =>
            contentTypesWhitelist.includes(d.kp_content_type)
          )
        : allTpls;


      
    // Then filter to only include templates with annotations
    return dataSource !== "annotations"
      ? filteredTpls
      : getTplsWithAnnosEnabled({ allTpls: filteredTpls });
  }, [allTpls, contentTypesWhitelist]);

  // State management
  const [selectedTpls, setSelectedTpls] = useState(tplOptions);

  // Persist template selection for this session (same convention as saved filters)
  useSavedTplSelection({
    module: "annoExplorer",
    storeKey: dataSource,
    optionIds: tplOptions.map((t) => t.kp_content_type),
    selectedIds: selectedTpls.map((t) => t.kp_content_type),
    restoreSelection: (ids) =>
      setSelectedTpls(tplOptions.filter((t) => ids.includes(t.kp_content_type))),
  });
  const [activeFilters, setActiveFilters] = useState([]);
  const totalFilters = [...presetActiveFilters, ...activeFilters];
  
  const [activeSort, setActiveSort] = useState(DEFAULT_SORT_OPTIONS[0].value);
  const [keyword, setKeyword] = useState("");

  // Lazy loading management
  const [cardsLimit, setCardsLimit] = useState(initialCardsLimit);
  const { observerRef } = useElemInView({
    callback: () => {
      setCardsLimit((prev) => prev + initialCardsLimit);
    },
  });

  // Reset cards limit when filters or selected templates change
  useEffect(() => {
    setCardsLimit(initialCardsLimit);
  }, [JSON.stringify(totalFilters), JSON.stringify(selectedTpls), initialCardsLimit]);

  // Determine fields to project from templates
   const annoFieldsToProject = tplOptions
      .map((tpl) => {
        // Annotation is only valid on publishing-segment tpls
        if (!isTplAnnotationEnabled(tpl)) return [];
        const allBlocks = extractAllBlocksFromTpl({ tpl });

        return blockRegistry.getAnnotationEnabledBlocks(allBlocks);
      })
      .reduce((a, b) => [...a, ...b], []);
  
  
    const mainFieldsToProject = tplOptions
      .map((tpl) => {
        const allBlocks = extractAllBlocksFromTpl({ tpl });
  
        return allBlocks.filter((bl) => ['RadioList', 'CheckboxList', 'DropdownSingle', 'DropdownMulti'].includes(bl.comp));
      })
      .reduce((a, b) => [...a, ...b], []);
    
  

  // Data fetching
  // const { data, status, error } = useGetDataQuery({
  //   contentTypes: selectedTpls.map((d) => d.kp_content_type),
  //   findQuery: { kp_published_status: "published" },
  //   population : [
  //     {
  //       path: "meta.kp_contributed_by",
  //       model: "user",
  //       select: ["name", "_id"],
  //     },
  //   ],
  //   translationConfig: {
  //     translateComp: [
  //       "TitleInput",
  //       "LexicalTextEditor",
  //       "TagsInputSingle",
  //       "TagsInputMulti",
  //     ],
  //   },
  //   projection: {
  //     // we project anno fields here only because we need to separate them out. we really dont need them in the final data
  //     ...annoFieldsToProject.reduce(
  //       (a, b) => ({ ...a, [`${b.valuePath}.annoData`]: 1 }),
  //       {}
  //     ),
  //     ...mainFieldsToProject.reduce(
  //       (a, b) => ({ ...a, [b.valuePath]: 1 }),
  //       {}
  //     ),
  //     // main: 1,
  //     tags: 1,
  //     "main.title": 1,
  //     "meta.kp_content_type": 1,
  //     "kp_date_published": 1,
  //     "meta.kp_contributed_by": 1,
  //     "annotations": 1,
  //     translations: 1,
  //   },
  // });

  // Separate annotations into independent documents
  // const separatedData = useMemo(() => {
  //   if (status === "success") {
  //     return separateEachAnnoIntoIndependentDoc({
  //       data,
  //       selectedTpls,
  //     });
  //   }
  // }, [status, data, selectedTpls]);



  
  // Sort data
  // const sortedData = useMemo(
  //   () => applyActiveSort({data: _filteredData, activeSort}),
  //   [_filteredData, activeSort]
  // );

  // Apply keyword search
  // const data_finalResults = useMemo(
  //   () => applyKeywordSearch({ data: sortedData, keyword }),
  //   [sortedData, keyword]
  // );

  // Generate filter configurations
  const autoGenFilters = useAutoGenFilterConfigsFromTpl({
    selectedTpls,
    enableAsyncLoading: true,
  });
  
  const filterConfigs = useMemo(() => {
    if (autoGenFilters?.loading) {
      return [];
    }

    const filterDefs = _filterDefs || getAnnoExplorerConfigsForThisTenant()?.filterDefs || {}
    
    return applyFilterConfigsAsPerTenant({autoGenFilters: autoGenFilters?.filterConfigs || autoGenFilters, filterDefs});
  }, [selectedTpls, autoGenFilters]);

  // Clean up stale activeFilters when filterConfigs change (template switch)
  useEffect(() => {
    if (filterConfigs.length === 0 || activeFilters.length === 0) return;
    const cleaned = cleanStaleActiveFilters({ activeFilters, filterConfig: filterConfigs });
    if (cleaned.length !== activeFilters.length) {
      setActiveFilters(cleaned);
    }
  }, [filterConfigs]); // intentionally omit activeFilters to avoid loops

  // Paginated results
  // const paginatedResults = useMemo(
  //   () =>
  //     data_finalResults ? [...data_finalResults].slice(0, cardsLimit) : undefined,
  //   [data_finalResults, cardsLimit]
  // );

  return {
    // Template options
    tplOptions,
    tplOptionsDropdownData: tplOptions.map((d) => ({
      display: d.general?.content?.title || d.kp_content_type,
      value: d.kp_content_type,
    })),

    // Data states
    // data: paginatedResults,
    // totalData: data_finalResults,
    // totalCount: data_finalResults?.length || 0,
    // error: status === "success" ? fetchTagTypeError : error,
    // dataFetchStatus: status === "success" ? fetchFilteredDataStatus : status,
    
    // Filtering and sorting states
    selectedTpls,
    setSelectedTpls,
    activeFilters,
    setActiveFilters,
    filterConfigs,
    areFiltersLoading: autoGenFilters?.loading || false,

    // Sorting states
    DEFAULT_SORT_OPTIONS,
    activeSort,
    setActiveSort,

    // Search states
    keyword,
    setKeyword,

    // Lazy loading
    observerRef,
    cardsLimit,
    setCardsLimit,
    dataSource

    // Fetch statuses
  };
};